/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, ValidatorFn } from '@angular/forms';
import { isArray } from 'lodash-es';
import { readFileAsJson } from '../utils/file';

export type FilesMaxSizeError = { expected: number; actual: number };

export const validateFilesMaxTotalSize =
  (maxTotalSizeInBytes: number): ValidatorFn =>
  (control) => {
    const isFormControlWithValues =
      control instanceof FormControl && isArray(control.value) && control.value.every((item) => item instanceof File);
    if (control instanceof FormArray || isFormControlWithValues) {
      const totalSizeInBytes = Array.from(control.value).reduce((acc: number, value) => {
        if (value instanceof File) {
          acc += value.size;
        }
        return acc;
      }, 0);
      return totalSizeInBytes < maxTotalSizeInBytes
        ? null
        : ({
            filesMaxTotalSize: { expected: maxTotalSizeInBytes, actual: totalSizeInBytes } as FilesMaxSizeError,
          } as const);
    } else {
      return null;
    }
  };

export const validateFileMaxSize =
  (maxSizeInBytes: number): ValidatorFn =>
  (control) => {
    if (control instanceof FormControl && control.value instanceof File) {
      const sizeInBytes = control.value.size;
      return sizeInBytes < maxSizeInBytes
        ? null
        : ({
            filesMaxTotalSize: { expected: maxSizeInBytes, actual: sizeInBytes },
          } as const);
    } else {
      return null;
    }
  };

export const asFilesMaxTotalSizeValidatorResut = (value: unknown) => value as FilesMaxSizeError;

export const validateFileFormat =
  <T>(validator: (value: unknown) => value is T): AsyncValidatorFn =>
  async (control: AbstractControl<File>): Promise<{ fileFormat: true } | null> => {
    if (!(control instanceof FormControl && control.value instanceof File)) {
      return null;
    }

    const file = control.value;

    try {
      const contents = await readFileAsJson(file);
      return validator(contents) ? null : { fileFormat: true };
    } catch (error) {
      return { fileFormat: true };
    }
  };
